// src/features/reels/components/reel-card.tsx
import { useTranslation } from "react-i18next";
import { cn } from "../../../lib/utils";
import { useDisplayPrice } from "../../../lib/currency";
import { ImageWithFallback } from "../../../shared/ui/image-with-fallback";
import { panelFrameStyle } from "../../../shared/ui/card-frame";
import { pewterInner, solid } from "../../../shared/ui/pewter-edge";
import { warmVideo } from "../lib/warm-video";

import { Icon } from "@majourneys/icons";
export interface ReelData {
  id: string;
  thumbnail?: string;
  videoUrl?: string;
  title: string;
  price: number;
  category: { id: string; name: string; slug: string };
  city: { id: string; name: string; slug: string };
  activitySlug: string;
}

interface ReelCardProps {
  reel: ReelData;
  onClick: () => void;
  className?: string;
}

// The card's flex-basis per breakpoint (see ReelsSection), so the browser
// downloads a poster no wider than the frame it lands in.
const POSTER_SIZES =
  "(max-width: 640px) 72vw, (max-width: 768px) 42vw, (max-width: 1024px) 29vw, (max-width: 1280px) 23vw, 19vw";

/**
 * The pewter frame, in two elements rather than one.
 *
 * The band has to sit OUTSIDE the clip: this card crops a photo to its rounded
 * corners, and a frame on the same node that does the cropping is cropped
 * along with it. So the button owns the band and the shadow, and an inner
 * element owns `overflow-hidden`.
 *
 * `topLit` because a reel rail is a repeating grid of small cards, which is the
 * case it is specified for. The house `pewter` turns as your eye travels round
 * it, and four turning bands side by side read as glitter rather than material.
 *
 * The inner hairline is white at low alpha, not the cards' navy: this card is a
 * dark surface, and a navy line on near-black is invisible.
 */
const REEL_RADIUS = 28;
const REEL_FRAME = panelFrameStyle(REEL_RADIUS, "topLit");
const REEL_INNER = pewterInner(solid("#040033"), REEL_RADIUS, {
  hairline: "rgba(255,255,255,0.14)",
});

export function ReelCard({ reel, onClick, className }: ReelCardProps) {
  const { t } = useTranslation();
  const displayPrice = useDisplayPrice();
  const videoUrl = reel.videoUrl?.trim();
  const thumbnail = reel.thumbnail?.trim();
  // A real poster is a still image distinct from the video file. With one, the
  // card is just that image: no <video> element, so the home page downloads
  // zero bytes of video until the popup opens. Without one (legacy rows where
  // thumbnail === videoUrl) fall back to the browser rendering the frame at
  // 0.5s, exactly as before.
  const hasRealPoster = !!thumbnail && thumbnail !== videoUrl;
  const warm = () => {
    if (videoUrl) warmVideo(videoUrl);
  };

  return (
    <button
      type="button"
      onClick={onClick}
      onPointerEnter={warm}
      onTouchStart={warm}
      onFocus={warm}
      className={cn(
        // `block w-full` is load-bearing, not tidying. A <button> is
        // inline-block and shrink-to-fit, so with no width of its own this
        // card took its width from the poster inside it rather than from the
        // carousel slide around it. Until that image loaded the card was 8px
        // wide in a 213px slide, so the rail drew slivers separated by the
        // empty rest of each slide and lost its height, which left a band of
        // white space above the next section (owner, 2026-09-14, "too much
        // space between videos"). Filling the slide puts the cards back at
        // one `gap-3` apart and lets `aspect-[9/16]` hold the row's height
        // while the posters are still downloading.
        "group relative block aspect-[9/16] w-full text-start transition duration-300 hover:-translate-y-1",
        className,
      )}
      style={REEL_FRAME}
    >
      <div
        className="relative h-full w-full overflow-hidden"
        style={REEL_INNER}
      >
        {hasRealPoster ? (
          <ImageWithFallback
            src={thumbnail}
            alt={reel.title}
            sizes={POSTER_SIZES}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : videoUrl ? (
          <video
            src={`${videoUrl}${videoUrl.includes("#") ? "" : "#t=0.5"}`}
            preload="metadata"
            muted
            playsInline
            aria-label={reel.title}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <ImageWithFallback
            src={thumbnail}
            alt={reel.title}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#040033]/90 via-[#040033]/10 to-transparent" />

        <div className="absolute top-3 left-3">
          <span className="rounded-full bg-white/90 px-3 py-1 font-sans text-[10.5px] font-bold uppercase tracking-[0.08em] text-[#040033] shadow-sm backdrop-blur-sm">
            {reel.category.name}
          </span>
        </div>

        <div className="absolute inset-0 flex items-center justify-center opacity-90 transition-opacity group-hover:opacity-100">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white/25 text-white shadow-[0_10px_30px_rgba(4,0,51,0.22)] ring-1 ring-white/40 backdrop-blur-md transition group-hover:scale-105 group-hover:bg-[#E5006E]">
            <Icon name="ri-play-fill" className="text-2xl" />
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-5">
          <h3 className="font-heading text-[16px] font-bold leading-[1.2] tracking-[-0.01em] text-white line-clamp-2">
            {reel.title}
          </h3>
          {/*
            Two spans in a flex row are both `min-width: auto` by default, so
            neither gives way: at two columns on a phone the card is about
            160px and "Marrakech" and "From 1,500 MAD" simply overlap, with the
            price breaking after the number so "MAD" lands on its own line.

            `flex-wrap` rather than making one of them give way. Squeezing the
            city truncated it to "A." at two columns, which is not a city name,
            and hiding the price is not an option since it is the reason someone
            taps. Wrapping puts them on one line when both fit and drops the
            price onto a second when they do not, so both stay readable.

            The price keeps `whitespace-nowrap` so it never breaks after the
            number and leaves "MAD" stranded on its own line.
          */}
          <div className="mt-1 flex flex-wrap items-center justify-between gap-x-2 gap-y-0.5">
            <span className="min-w-0 break-words font-sans text-[13px] text-white/80">
              {reel.city.name}
            </span>
            {reel.price > 0 ? (
              <span className="shrink-0 whitespace-nowrap font-sans text-[14px] font-bold text-white">
                {t("activity.from", "From")} {displayPrice(reel.price)}
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </button>
  );
}
